import {
  Divider,
  Flex,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tag,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import type { SelectionList } from '@revelio/iso-utility';
import { Text } from '@revelio/ui';

import { useSelectionLists } from '../../shared/selection-lists/hooks/useSelectionLists';
import type { GqlSelectionListKey } from '../../shared/selection-lists/types';
import {
  buildFieldGranularityMap,
  buildFieldLabelMap,
} from './api/columns-api-helpers';
import type {
  DataBuilderStatus as DataBuilderApiStatus,
  DataBuilderPayload,
} from './api/data-builder-api.types';
import { useDataBuilderQuery } from './api/useDataBuilderQuery';
import { useRoleSelectionLists } from './api/useRoleSelectionLists';
import { useSkillSelectionLists } from './api/useSkillSelectionLists';
import {
  classifyKey,
  resolveSelectionListItemLabel,
  toFetchKey,
} from './components/custom-filters/data-builder-selection-list-mapping';
import type { ConfigSummaryDetail } from './components/ui/ConfigSummaryList';
import { ConfigSummaryList } from './components/ui/ConfigSummaryList';
import type { DatasetSummaryData } from './components/ui/DatasetSummaryCard';
import { DatasetSummaryCard } from './components/ui/DatasetSummaryCard';
import {
  dataBuilderTagStatusColorMap,
  defaultTagColors,
  getDataBuilderStatusLabel,
  isDeliveryDownloadable,
} from './DataBuilderStatus';
import type {
  CustomFilterCondition,
  DeliveryMethod,
  ModelVersionSelections,
} from './data-builder.store';
import type { ConfirmationSummaryFromPayload } from './steps/buildConfirmationSummaryFromPayload';
import { buildConfirmationSummaryFromPayload } from './steps/buildConfirmationSummaryFromPayload';
import {
  getVersionDisplayLabel,
  isLatestVersions,
} from './steps/step2-column/model-utils';
import { dataLagDisplayValue } from './utils/data-lag-helper';
import { isV3Taxonomy } from './utils/job-taxonomy-version-migration';

function extractSelectionListKeys(summaries: DatasetSummaryData[]): string[] {
  const keys = new Set<string>();
  const collectFromConditions = (conditions: CustomFilterCondition[]) => {
    for (const c of conditions) {
      if (c.type === 'selection_list' && c.selectionListItems?.length) {
        for (const item of c.selectionListItems) {
          keys.add(String(toFetchKey(item.selectionListId)));
        }
      }
    }
  };
  for (const ds of summaries) {
    // Unit of analysis selections
    for (const item of ds.unitOfAnalysisSelections) {
      if (item.selectionListId) {
        keys.add(String(toFetchKey(item.selectionListId)));
      }
    }
    for (const group of ds.filterGroups) {
      for (const f of group.filters) collectFromConditions(f.conditions);
    }
    for (const group of ds.customColumnGroups) {
      for (const col of group.customColumns) {
        for (const step of col.steps) {
          if (step.case) collectFromConditions(step.case.conditions);
        }
      }
    }
  }
  return Array.from(keys);
}

function useStatusSelectionLists(
  summary: ConfirmationSummaryFromPayload | null
): SelectionList[] {
  const allKeys = useMemo(
    () => (summary ? extractSelectionListKeys(summary.datasetSummaries) : []),
    [summary]
  );

  const modelVersions = (summary?.modelVersions ??
    {}) as ModelVersionSelections;
  const jobTaxVersion = String(modelVersions.job_taxonomy_version ?? '');
  const skillTaxVersion = String(modelVersions.skill_taxonomy_version ?? '');
  const isV3Skill = isV3Taxonomy(skillTaxVersion);

  const { gqlKeys, roleKeys, skillKeys } = useMemo(() => {
    const gql: GqlSelectionListKey[] = [];
    const role: string[] = [];
    const skill: string[] = [];
    for (const k of allKeys) {
      const cat = classifyKey(k);
      if (cat === 'role') role.push(k);
      else if (cat === 'skill') skill.push(k);
      else gql.push(k as GqlSelectionListKey);
    }
    // Also include non-v3 skills as GQL keys
    if (!isV3Skill) {
      gql.push(...(skill as GqlSelectionListKey[]));
      skill.length = 0;
    }
    return { gqlKeys: gql, roleKeys: role, skillKeys: skill };
  }, [allKeys, isV3Skill]);

  // Standard GQL lists
  const gqlState = useSelectionLists(gqlKeys);

  // Role lists
  const { roleSelectionLists } = useRoleSelectionLists(roleKeys, {
    jobTaxonomyVersion: jobTaxVersion,
    taxonomyId: modelVersions.job_taxonomy_id as number | undefined,
  });

  // V3 Skill lists
  const { skillSelectionLists } = useSkillSelectionLists(skillKeys, {
    skillTaxonomyVersion: skillTaxVersion,
    taxonomyId: modelVersions.skill_taxonomy_id as number | undefined,
  });

  return useMemo(() => {
    const result: SelectionList[] = [];

    if (gqlState) {
      for (const key of gqlKeys) {
        const list = gqlState[key as keyof typeof gqlState];
        if (list) {
          const normalizedValue = (list[0] as SelectionList)?.label
            ? list
            : list.map((item) => ({ ...item, label: item.longName }));
          result.push({
            id: key,
            label: key,
            value: normalizedValue,
          } as SelectionList);
        }
      }
    }

    if (roleSelectionLists) {
      Object.values(roleSelectionLists).forEach((list) => {
        if (list) result.push(list as SelectionList);
      });
    }

    if (skillSelectionLists) {
      Object.values(skillSelectionLists).forEach((list) => {
        if (list) result.push(list);
      });
    }

    return result;
  }, [gqlState, gqlKeys, roleSelectionLists, skillSelectionLists]);
}

const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  download: 'Download',
  snowflake: 'Snowflake',
  s3: 'AWS S3',
  sftp: 'SFTP',
};

type DataBuilderStatusDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  client: string;
  status: DataBuilderApiStatus;
  url: string | null;
  payload: DataBuilderPayload;
};

export const DataBuilderStatusDetailsModal = ({
  isOpen,
  onClose,
  name,
  client,
  status,
  url,
  payload,
}: DataBuilderStatusDetailsModalProps) => {
  const { data } = useDataBuilderQuery();
  const models = useMemo(() => data?.models ?? [], [data?.models]);
  const deliverablesConfig = useMemo(
    () => data?.deliverablesConfig ?? {},
    [data?.deliverablesConfig]
  );

  const summary = useMemo(
    () => (isOpen ? buildConfirmationSummaryFromPayload(payload) : null),
    [isOpen, payload]
  );

  // Resolve model version labels, field labels, breakdown labels using API data
  const enhancedSummary = useMemo(() => {
    if (!summary) return null;

    const allLatest = isLatestVersions(summary.modelVersions, models);
    const resolvedModelEntries = Object.entries(summary.modelVersions).map(
      ([key, value]) => {
        const model = models.find((m) => m.key === key);
        const modelLabel = model?.name ?? key;
        const versionLabel =
          model && (typeof value === 'number' || typeof value === 'string')
            ? getVersionDisplayLabel(model, value)
            : String(value);
        return { modelLabel, versionLabel };
      }
    );

    const datasetSummaries = summary.datasetSummaries.map((ds) => {
      const deliverableConfig = deliverablesConfig[ds.datasetKey];

      // Resolve column breakdown labels
      const columns = ds.columns.map((col) => {
        if (col.breakdownId === 'aggregation') return col;
        const breakdown = deliverableConfig?.breakdowns.find(
          (b) => b.id === col.breakdownId
        );
        return breakdown ? { ...col, breakdownLabel: breakdown.label } : col;
      });

      // Granularities
      const granularities = ds.columns
        .filter((col) => col.breakdownId !== 'aggregation')
        .map((col) => {
          const breakdown = deliverableConfig?.breakdowns.find(
            (b) => b.id === col.breakdownId
          );
          return breakdown?.label ?? col.breakdownLabel;
        });

      // Resolve unit of analysis label
      const unitOfAnalysis = (() => {
        if (!ds.unitOfAnalysis || ds.unitOfAnalysis === 'default') {
          return ds.unitOfAnalysis;
        }
        const option = deliverableConfig?.unitOfAnalysis.options.find(
          (opt) => opt.value === ds.unitOfAnalysis
        );
        return option?.label ?? ds.unitOfAnalysis;
      })();

      // Build unified field label map from ALL filter definitions
      const allFilterDefs = [
        ...(deliverableConfig?.generalFilters.customFilterDefinitions ?? []),
        ...(deliverableConfig?.breakdowns.flatMap(
          (b) => b.customFilterDefinitions
        ) ?? []),
        ...(deliverableConfig?.breakdowns.flatMap(
          (b) => b.customColumnFilterDefinitions
        ) ?? []),
      ];
      const unifiedFieldLabelMap = buildFieldLabelMap(allFilterDefs);
      const unifiedFieldGranularityMap =
        buildFieldGranularityMap(allFilterDefs);

      const filterGroups = ds.filterGroups.map((group) => {
        const breakdown =
          group.breakdownLabel !== null
            ? deliverableConfig?.breakdowns.find(
                (b) =>
                  b.label === group.breakdownLabel ||
                  b.id === group.breakdownLabel
              )
            : undefined;
        return {
          ...group,
          ...(breakdown ? { breakdownLabel: breakdown.label } : {}),
          fieldLabelMap: unifiedFieldLabelMap,
          fieldGranularityMap: unifiedFieldGranularityMap,
        };
      });

      const customColumnGroups = ds.customColumnGroups.map((group) => {
        const breakdown = deliverableConfig?.breakdowns.find(
          (b) =>
            b.label === group.breakdownLabel || b.id === group.breakdownLabel
        );
        return {
          ...group,
          ...(breakdown ? { breakdownLabel: breakdown.label } : {}),
          fieldLabelMap: unifiedFieldLabelMap,
          fieldGranularityMap: unifiedFieldGranularityMap,
        };
      });

      return {
        ...ds,
        unitOfAnalysis,
        columns,
        granularities,
        filterGroups,
        customColumnGroups,
        modelVersions: resolvedModelEntries,
        allLatestModelVersions: allLatest,
      };
    });

    return { ...summary, datasetSummaries };
  }, [summary, models, deliverablesConfig]);

  const selectionLists = useStatusSelectionLists(summary);

  // Resolve unit of analysis selection labels (IDs -> names)
  const finalSummary = useMemo(() => {
    if (!enhancedSummary || selectionLists.length === 0) return enhancedSummary;

    return {
      ...enhancedSummary,
      datasetSummaries: enhancedSummary.datasetSummaries.map((ds) => ({
        ...ds,
        unitOfAnalysisSelections: ds.unitOfAnalysisSelections.map((item) => {
          if (item.label !== item.id) return item;
          const resolved = resolveSelectionListItemLabel(
            item.id,
            item.selectionListId,
            selectionLists
          );
          return resolved ? { ...item, label: resolved } : item;
        }),
      })),
    };
  }, [enhancedSummary, selectionLists]);

  if (finalSummary === null) {
    return null;
  }

  const {
    datasetSummaries,
    deliveryMethod,
    deliveryFormat,
    snowflakeConfig,
    s3Config,
    sftpConfig,
    lagData,
  } = finalSummary;

  const deliveryDetails: ConfigSummaryDetail[] = (() => {
    const methodLabel = DELIVERY_METHOD_LABELS[deliveryMethod];
    const dataLagDetails =
      lagData > 0
        ? [
            {
              label: 'Data lag',
              value: dataLagDisplayValue(lagData),
            },
          ]
        : [];

    if (deliveryMethod === 'snowflake' && snowflakeConfig !== null) {
      const fullTablePath = `${snowflakeConfig.database}.${snowflakeConfig.schema}.${snowflakeConfig.table}`;
      return [
        { label: 'Method', value: methodLabel },
        { label: 'Table', value: fullTablePath, copyValue: fullTablePath },
        ...dataLagDetails,
      ];
    }

    if (deliveryMethod === 's3' && s3Config !== null) {
      const fullS3Path = `s3://${s3Config.bucket}/${s3Config.path.replace(/^\//, '')}`;
      return [
        { label: 'Method', value: methodLabel },
        { label: 'Path', value: fullS3Path, copyValue: fullS3Path },
        { label: 'Format', value: s3Config.format },
        ...dataLagDetails,
      ];
    }

    if (deliveryMethod === 'sftp' && sftpConfig !== null) {
      return [
        { label: 'Method', value: methodLabel },
        {
          label: 'Path',
          value: sftpConfig.path,
          copyValue: sftpConfig.path,
        },
        { label: 'Format', value: sftpConfig.format },
        ...dataLagDetails,
      ];
    }

    const downloadValue = isDeliveryDownloadable(status, url) ? (
      <Link
        href={url ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        color="text.primary"
        textDecoration="underline"
      >
        {`${methodLabel} (${deliveryFormat})`}
      </Link>
    ) : (
      `${methodLabel} (${deliveryFormat})`
    );

    return [{ label: 'Delivery', value: downloadValue }, ...dataLagDetails];
  })();

  const palette = dataBuilderTagStatusColorMap[status] ?? defaultTagColors;

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent minHeight="400px">
        <ModalHeader
          borderBottom="1px solid #E5EBF1"
          fontSize="17px"
          fontWeight="600"
          py="12px"
        >
          <Flex alignItems="center" gap={3}>
            <span>
              {client} - {name}
            </span>
            <Tag
              bg={palette.bgColor}
              color={palette.color}
              borderRadius="4px"
              fontWeight="semibold"
              minWidth="94px"
              textAlign="center"
              py={1}
              justifyContent="center"
            >
              {getDataBuilderStatusLabel(status)}
            </Tag>
          </Flex>
        </ModalHeader>
        <ModalCloseButton size="sm" />
        <ModalBody py="16px">
          <Heading fontWeight="600" fontSize="16px" color="text.primary">
            Datasets
          </Heading>

          {datasetSummaries.map((dataset) => (
            <DatasetSummaryCard
              key={dataset.instanceId}
              dataset={dataset}
              selectionLists={selectionLists}
            />
          ))}

          {datasetSummaries.length === 0 && (
            <Text fontSize="sm" style={{ paddingLeft: '20px' }}>
              No datasets selected
            </Text>
          )}

          <Divider my="4" />

          <Heading fontWeight="600" fontSize="16px" color="text.primary">
            Delivery
          </Heading>
          <ConfigSummaryList details={deliveryDetails} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
